// What the contents show (the sidebar redesign's source picker, `design-docs/Sidebar redesign — design.md`; it replaces
// the arrangement menu): a button with a tile, "BINDER" or "COLLECTION" and the name, opening a panel — the
// arrangements (a check on the one shown while no collection is loaded), the collections (each in its colour and icon,
// with how many it holds now), the tags (folded; each with its count, choosing one filters by it), New Collection…,
// Edit and Delete for the collection loaded, then the arrangement's own items: Fork “…”, Make This the Manuscript for
// a fork, New from Template. Choosing an arrangement while a collection is loaded clears the filter; choosing a
// collection loads it into the filter (a manuscript one shows the manuscript, a research one Research & Notes). The
// shell gives it this side's arrangements (Research's reference ones, or the rest; Plan's, all); the collections follow
// their scope (Plan's, every one).
//
// It also answers Find in Project's Save as Collection, and Structure ▸ Next/Previous Status (⌃⌘] / ⌃⌘[;
// Ctrl+Alt+] / Ctrl+Alt+[ elsewhere) wherever the contents are.
import { useEffect, useRef, useState } from 'react';
import type { ArrangementInfo, OpenedProject } from '@writing-suite/core';
import { collectionIcon, collectionInk, newRule, savedSearchesOf, type SavedSearch } from '@writing-suite/core/plan';
import { announce } from '../../shell/context.ts';
import { formatRoute, navigate } from '../../shell/route.ts';
import { useWorkspace } from '../../shell/workspace.ts';
import { showsEitherSide, showsResearch, type RoomId } from '../../rooms/rooms.ts';
import { planEdit, takeCollectionRequest, useCollectionRequest } from '../../store/plan.ts';
import { arrangementSetOf, membersOf, nextVersionName, projectTags } from '../../store/planModel.ts';
import { formatNumber } from '../helpers.ts';
import { Icon, Tile, type IconName, type Ink } from '../Icons.tsx';
import { TextDialog } from '../Margin.tsx';
import { MenuPopup, type MenuEntry } from '../Menu.tsx';
import { DeleteCollectionDialog, type CollectionDialogState } from '../FilterBar.tsx';
import { CollectionEditor } from './CollectionEditor.tsx';
import { statusKey, stepStatus } from './menus.ts';
import { templateEntries } from './templates.ts';

/** An arrangement's symbol, by its role. */
export const ROLE_ICONS: Record<ArrangementInfo['role'], IconName> = {
  manuscript: 'book', reference: 'notes', publishing: 'send', alternate: 'fork', trash: 'trash', collection: 'stack',
};

export function SourcePicker({ projectId, pageForNode, room, arrangements, onArrangement }: {
  projectId: string;
  pageForNode: string;
  room: RoomId;
  /** The arrangements the picker offers, in order. */
  arrangements: ArrangementInfo[];
  /** Shows an arrangement (the shell navigates). */
  onArrangement: (a: ArrangementInfo) => void;
}) {
  const ws = useWorkspace();
  const [open, setOpen] = useState<{ x: number; y: number; minWidth: number } | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [dialog, setDialog] = useState<CollectionDialogState | null>(null);
  const [deleting, setDeleting] = useState<SavedSearch | null>(null);
  const [forking, setForking] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const request = useCollectionRequest();

  // Find in Project's Save as Collection.
  useEffect(() => {
    if (!request) return;
    takeCollectionRequest();
    const rules = request.rules ?? [newRule({ kind: 'text', value: request.text })];
    setDialog({ search: null, draft: { name: request.text, scope: request.scope, rules } });
  }, [request]);

  // Next/Previous Status, wherever the contents are.
  const wsRef = useRef(ws);
  wsRef.current = ws;
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const step = statusKey(e);
      const w = wsRef.current;
      if (!step || !w) return;
      e.preventDefault();
      stepStatus(w, step === 'next', room);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [room]);

  if (!ws) return null;
  const project = ws.project as OpenedProject;
  const live = ws.live;
  const arrangement = ws.arrangement;
  const filter = ws.filter;
  const shown = filter?.saved ?? null;
  const writable = !!live && ws.canWrite;
  const forkSet = () => (project.record ? arrangementSetOf(project, arrangement.id) : null);
  const go = (arrangementId: string | null, nodeId: string | null = null, page = pageForNode) => {
    const [r, p] = page.split('/');
    navigate(formatRoute({ name: 'project', projectId, room: r as RoomId, page: p, nodeId, arrangementId: arrangementId === project.manuscriptId() ? null : arrangementId, params: {} }));
  };
  const ink: Ink = shown ? (collectionInk(shown) as Ink) : 'accent';
  const kind = shown ? 'Collection' : 'Shelf';
  const name = shown ? shown.name : arrangement.name;

  // Read when the panel opens (never on the rebuilds typing makes).
  const entries = (): MenuEntry[] => {
    const research = showsResearch(room);
    const searches = project.record ? savedSearchesOf(project.record.settings).filter((s) => showsEitherSide(room) || (s.scope === 'research') === research) : [];
    const tags = project.record ? projectTags(project) : [];
    const armSet = forkSet();
    const member = armSet?.members.find((m) => m.arrangementID === arrangement.id);
    const out: MenuEntry[] = [{ kind: 'heading', label: 'Shelf' }];
    for (const a of arrangements) {
      out.push({
        kind: 'radio',
        label: a.name,
        lead: <Icon name={ROLE_ICONS[a.role]} size={15} />,
        checked: a.id === arrangement.id && !shown,
        onSelect: () => {
          if (shown) filter?.clear();
          if (a.id !== arrangement.id) onArrangement(a);
        },
      });
    }
    if (searches.length) {
      out.push({ kind: 'separator' }, { kind: 'heading', label: 'Collections' });
      for (const s of searches) {
        out.push({
          kind: 'radio',
          label: s.name,
          lead: <Tile icon={collectionIcon(s)} ink={collectionInk(s) as Ink} size={22} />,
          detail: formatNumber(membersOf(project, s).length),
          checked: shown?.id === s.id,
          onSelect: () => filter?.load(s),
        });
      }
    }
    if (tags.length) {
      out.push(
        { kind: 'separator' },
        { label: 'Tags', lead: <Icon name="number" size={15} />, keepOpen: true, expanded: tagsOpen, detail: tagsOpen ? '' : formatNumber(tags.length), onSelect: () => setTagsOpen(!tagsOpen) },
      );
      if (tagsOpen) {
        for (const t of tags) {
          out.push({
            label: t.tag,
            lead: <span className="menu-indent" />,
            detail: formatNumber(t.count),
            onSelect: () => filter?.filterBy([newRule({ kind: 'field', field: 'tags', value: t.tag })]),
          });
        }
      }
    }
    out.push(
      { kind: 'separator' },
      { label: 'New Collection…', lead: <Icon name="plus" size={15} />, accent: true, disabled: !writable, onSelect: () => setDialog({ search: null }) },
    );
    if (shown) {
      out.push(
        { label: `Edit “${shown.name}”…`, disabled: !writable, onSelect: () => setDialog({ search: shown }) },
        { label: `Delete “${shown.name}”…`, danger: true, disabled: !writable, onSelect: () => setDeleting(shown) },
      );
    }
    const own: MenuEntry[] = [
      ...(arrangement.role === 'manuscript' || arrangement.role === 'alternate'
        ? [{ label: `Try Another Order of “${arrangement.name}”…`, lead: <Icon name="fork" size={15} />, disabled: !ws.canStructure, onSelect: () => setForking(true) }]
        : []),
      ...(armSet && member && member.id !== armSet.currentMemberID
        ? [
            {
              label: `Make This the ${armSet.role === 'manuscript' || !armSet.role ? 'Manuscript' : armSet.role === 'reference' ? 'Research' : 'Arrangement'}`,
              disabled: !writable,
              onSelect: () => {
                planEdit(live!, (e) => e.promote(member.id, armSet.id));
                announce(`“${arrangement.name}” is now the manuscript`);
                go(null);
              },
            },
          ]
        : []),
    ];
    if (own.length) out.push({ kind: 'separator' }, ...own);
    const templates = templateEntries({
      project: ws.project, live, arrangement, node: ws.node, canStructure: ws.canStructure,
      opened: (nodeId) => go(arrangement.id, nodeId, 'write/write'),
    });
    if (templates.length) out.push({ kind: 'separator' }, ...templates);
    return out;
  };

  const show = () => {
    const r = button.current!.getBoundingClientRect();
    setOpen({ x: r.left, y: r.bottom + 4, minWidth: r.width });
  };
  const close = (restore: boolean) => {
    setOpen(null);
    setTagsOpen(false);
    if (restore) button.current?.focus();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`source-picker tint-${ink}`}
        aria-label={`${kind}: ${name}`}
        title="Choose what the shelf shows"
        aria-haspopup="menu"
        aria-expanded={!!open}
        data-source={shown ? 'collection' : 'binder'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => (open ? close(false) : show())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            show();
          }
        }}
      >
        <Tile icon={shown ? collectionIcon(shown) : ROLE_ICONS[arrangement.role]} ink={ink} size={24} />
        <span className="source-picker-text">
          <span className="source-picker-kind">{kind}</span>
          <span className="source-picker-name">{name}</span>
        </span>
        <Icon name="chevron" size={14} className="source-picker-chevron" />
      </button>
      {open ? <MenuPopup entries={entries()} at={open} label="Show on the shelf" onClose={close} opener={button.current} className="source-menu" /> : null}
      {dialog && project.record ? (
        <CollectionEditor
          project={project}
          search={dialog.search}
          draft={dialog.draft}
          onCancel={() => setDialog(null)}
          onSave={(s) => {
            setDialog(null);
            if (!live) return;
            planEdit(live, (e) => e.putSavedSearch(s));
            announce(`Collection “${s.name}” saved`);
            // Shown at once, as the filter.
            ws.filter?.load(s);
          }}
        />
      ) : null}
      {deleting ? <DeleteCollectionDialog ws={ws} search={deleting} onDone={() => setDeleting(null)} /> : null}
      {forking && live ? (
        <TextDialog
          title={`Try Another Order of “${arrangement.name}”`}
          label="Version"
          initial={nextVersionName(forkSet())}
          confirm="Create"
          message={<p>A copy of the arrangement, with the same documents, to reorder however you like. Make it the manuscript when it’s the book.</p>}
          onCancel={() => setForking(false)}
          onDone={(versionName) => {
            setForking(false);
            const set = planEdit(live, (e) => e.forkArrangement(arrangement.id, versionName));
            const made = set.members[set.members.length - 1];
            announce(`Made “${versionName}”, another order of “${arrangement.name}”`);
            if (made?.arrangementID) go(made.arrangementID);
          }}
        />
      ) : null}
    </>
  );
}
